import React, { useCallback, useMemo, useRef, useState } from 'react'
import Group from 'components/utils/Group'
import { pluralizeToString } from 'lib/string/pluralize'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import OfferFilterSelectBox from 'components/OfferPage/Filters/OfferFilterSelectBox'
import TravellersDesktop from '../shared/travellers/Desktop'
import RoomsDesktop from '../shared/rooms/Desktop'
import { useMultiRoomSelectionOfferPage } from 'businessTraveller/hooks/useMultiRoomSelection'

interface Props {
  className?: string
}

type MenuState = 'closed' | 'travellers' | 'rooms'

function BusinessTravellerSelectHotelsOfferPageDesktop({ className }: Props) {
  const {
    travellerEmployees,
    guestCount,
    totalTravellers,
    occupancies,
    roomCount,
    onAddEmployee,
    onRemoveEmployee,
    onAddGuest,
    onRemoveGuest,
    onRoomCountChange,
  } = useMultiRoomSelectionOfferPage()

  const travellerInputRef = useRef<HTMLButtonElement>(null)
  const roomInputRef = useRef<HTMLButtonElement>(null)
  const [activeMenu, setActiveMenu] = useState<MenuState>('closed')

  const openTravellerMenu = useCallback(() => {
    setActiveMenu('travellers')
  }, [])

  const openRoomMenu = useCallback(() => {
    setActiveMenu('rooms')
  }, [])

  const closeMenu = useCallback(() => {
    setActiveMenu('closed')
  }, [])

  const handleSelectTravellerApply = useCallback(() => {
    if (totalTravellers > 1) {
      setActiveMenu('rooms')
    } else {
      closeMenu()
    }
  }, [totalTravellers, closeMenu])

  const travellerValue = useMemo(() => {
    if (totalTravellers === 0) {
      return undefined
    }
    return <b>{totalTravellers} {totalTravellers === 1 ? 'adult' : 'adults'}</b>
  }, [totalTravellers])

  const roomValue = useMemo(() => {
    if (roomCount === 0) {
      return undefined
    }
    return <b>{pluralizeToString('room', roomCount)}</b>
  }, [roomCount])

  return (
    <Group className={className} direction="horizontal" gap={8}>
      <OfferFilterSelectBox
        ref={travellerInputRef}
        onClick={openTravellerMenu}
        active={activeMenu === 'travellers'}
        icon={<LineUserIcon />}
        label="Travellers"
        placeholder="Select travellers"
        value={travellerValue}
      />
      <OfferFilterSelectBox
        ref={roomInputRef}
        onClick={openRoomMenu}
        active={activeMenu === 'rooms'}
        icon={<LineBedIcon />}
        label="Rooms"
        placeholder="Select rooms"
        value={roomValue}
      />
      <TravellersDesktop
        open={activeMenu === 'travellers'}
        onClose={closeMenu}
        onApply={handleSelectTravellerApply}
        inputRef={travellerInputRef}
        totalTravellers={totalTravellers}
        onAddEmployee={onAddEmployee}
        onRemoveEmployee={onRemoveEmployee}
        onAddGuest={onAddGuest}
        onRemoveGuest={onRemoveGuest}
        travellerEmployees={travellerEmployees}
        guestCount={guestCount}
      />
      <RoomsDesktop
        open={activeMenu === 'rooms'}
        onClose={closeMenu}
        onApply={closeMenu}
        roomInputRef={roomInputRef}
        rooms={occupancies}
        onRoomCountChange={onRoomCountChange}
        travellerCount={totalTravellers}
      />
    </Group>
  )
}

export default BusinessTravellerSelectHotelsOfferPageDesktop
